import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { API_BASE_URL, FONT_UI, FONT_HEADING } from "../layouts/constants";

function getToken() {
  return (
    localStorage.getItem("guardToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("adminToken")
  );
}

function isGuardSession() {
  return localStorage.getItem("isGuardLoggedIn") === "true";
}

export default function PatrolQRCodePage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [checkpoints, setCheckpoints] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("all");
  const [qrImages, setQrImages] = useState({});
  const [loadingQr, setLoadingQr] = useState({});
  const errorShownRef = useRef(false);

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const isGuardLoggedIn = localStorage.getItem("isGuardLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const guardData = (() => {
      try {
        return JSON.parse(localStorage.getItem("guardData") || "{}");
      } catch {
        return {};
      }
    })();

    if (!token || (!isLoggedIn && !isGuardLoggedIn)) {
      navigate("/");
      return;
    }

    const local = isGuardSession()
      ? {
          company_name:
            guardData.fullName ||
            guardData.name ||
            guardData.fld_name ||
            localStorage.getItem("guardName") ||
            "Guard",
          licensedFor: "Guard QR Access",
          admin_active: 1,
          company_address: "",
          company_phone: "",
          admin_email:
            guardData.email || guardData.fld_mail || localStorage.getItem("guardEmail") || "",
          company_logo: "",
          user_map: localStorage.getItem("user_map") || "",
        }
      : {
          company_name:
            localStorage.getItem("company_name") || userData.company_name || "",
          licensedFor:
            localStorage.getItem("licensedFor") || userData.licensedFor || "",
          admin_active: Number(
            localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
          ),
          company_address:
            localStorage.getItem("company_address") ||
            userData.company_address ||
            "",
          company_phone:
            localStorage.getItem("company_phone") || userData.company_phone || "",
          admin_email:
            localStorage.getItem("admin_email") || userData.admin_email || "",
          company_logo:
            localStorage.getItem("company_logo") || userData.company_logo || "",
          user_map: localStorage.getItem("user_map") || userData.user_map || "",
        };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  const normalizeQRList = (result) => {
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result)) return result;
    return [];
  };

  const fetchQRImage = async (qrPath, id) => {
    if (!qrPath) return;

    try {
      setLoadingQr((prev) => ({ ...prev, [id]: true }));
      const token = getToken();

      // If it's a full URL, use it directly
      if (qrPath.startsWith("http")) {
        const response = await fetch(qrPath, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch QR: ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setQrImages((prev) => ({ ...prev, [id]: url }));
        return;
      }

      // Otherwise, construct the URL
      const baseUrl = API_BASE_URL.replace(/\/api$/, "");
      const imageUrl = `${baseUrl}${qrPath}`;

      const response = await fetch(imageUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch QR: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setQrImages((prev) => ({ ...prev, [id]: url }));
    } catch (error) {
      console.error(`Error fetching QR for ${id}:`, error);
      // If fetch fails, try using the URL directly as an image src
      const baseUrl = API_BASE_URL.replace(/\/api$/, "");
      const imageUrl = `${baseUrl}${qrPath}`;
      setQrImages((prev) => ({ ...prev, [id]: imageUrl }));
    } finally {
      setLoadingQr((prev) => ({ ...prev, [id]: false }));
    }
  };

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);
      errorShownRef.current = false;

      const guardSession = isGuardSession();

      // Guards don't have access to the admin-only company dashboard endpoint,
      // and shouldn't need it — skip that call entirely for guard sessions.
      const dashboardPromise = guardSession
        ? Promise.resolve(null)
        : fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

      const [dashboardRes, checkpointRes, routeRes] = await Promise.all([
        dashboardPromise,
        fetch(`${API_BASE_URL}/qrcode/patrol/checkpoints`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/qrcode/patrol/routes`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const checkpointResult = await checkpointRes.json();
      const routeResult = await routeRes.json();

      if (!checkpointRes.ok || checkpointResult.success === false) {
        throw new Error(
          checkpointResult.message || "Failed to fetch checkpoint QR"
        );
      }

      if (!routeRes.ok || routeResult.success === false) {
        throw new Error(routeResult.message || "Failed to fetch route QR");
      }

      if (!guardSession && dashboardRes) {
        const dashboardResult = await dashboardRes.json();

        if (!dashboardRes.ok || dashboardResult.success === false) {
          throw new Error(dashboardResult.message || "Failed to fetch dashboard");
        }

        const companyDetails = dashboardResult?.data?.companyDetails || {};

        setDashboardData({
          ...local,
          company_name: companyDetails.company_name ?? local.company_name ?? "",
          licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
          admin_active: Number(
            companyDetails.admin_active ?? local.admin_active ?? 0
          ),
          company_address:
            companyDetails.company_address ?? local.company_address ?? "",
          company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
          admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
          company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
          user_map: companyDetails.user_map ?? local.user_map ?? "",
        });
      }

      const checkpointList = normalizeQRList(checkpointResult);
      const routeList = normalizeQRList(routeResult);

      setCheckpoints(checkpointList);
      setRoutes(routeList);

      // Pre-fetch QR images
      const allItems = [...checkpointList, ...routeList];
      allItems.forEach((item) => {
        const qrPath = item.qr_image_url || item.qr_code_id || item.qr_image;
        if (qrPath && item.id) {
          fetchQRImage(qrPath, item.id);
        }
      });

      toast.success("QR codes loaded successfully!");
    } catch (error) {
      console.error("Patrol QR page error:", error);
      toast.error(error.message || "Failed to load patrol QR codes.");
      errorShownRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google" ? "/route-master" : "/route-master1",
    [dashboardData.user_map]
  );

  const stats = useMemo(
    () => ({
      checkpointCount: checkpoints.length,
      routeCount: routes.length,
      totalCount: checkpoints.length + routes.length,
    }),
    [checkpoints, routes]
  );

  // Build unique route options from both routes and checkpoints
  const routeOptions = useMemo(() => {
    const map = new Map();
    routes.forEach((item) => {
      const data = item.qr_data || {};
      const id = String(data.route_id || item.reference_id || item.id || "");
      const name = data.route_name || item.reference_name || id;
      if (id && !map.has(id)) map.set(id, name);
    });
    checkpoints.forEach((item) => {
      const data = item.qr_data || {};
      const id = String(data.route_id || "");
      const name = data.route_name || id;
      if (id && !map.has(id)) map.set(id, name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [routes, checkpoints]);

  // Apply text search
  const textFilteredCheckpoints = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return checkpoints;
    return checkpoints.filter((item) => {
      const data = item.qr_data || {};
      return (
        String(item.reference_id || "").toLowerCase().includes(keyword) ||
        String(item.reference_name || "").toLowerCase().includes(keyword) ||
        String(item.qr_type || "").toLowerCase().includes(keyword) ||
        String(data.route_id || "").toLowerCase().includes(keyword) ||
        String(data.route_name || "").toLowerCase().includes(keyword) ||
        String(data.checkpoint_id || "").toLowerCase().includes(keyword) ||
        String(data.checkpoint_name || "").toLowerCase().includes(keyword)
      );
    });
  }, [checkpoints, search]);

  const textFilteredRoutes = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return routes;
    return routes.filter((item) => {
      const data = item.qr_data || {};
      return (
        String(item.reference_id || "").toLowerCase().includes(keyword) ||
        String(item.reference_name || "").toLowerCase().includes(keyword) ||
        String(item.qr_type || "").toLowerCase().includes(keyword) ||
        String(data.route_id || "").toLowerCase().includes(keyword) ||
        String(data.route_name || "").toLowerCase().includes(keyword)
      );
    });
  }, [routes, search]);

  // Build grouped view: [ { routeId, routeName, routeQR, checkpointQRs } ]
  const groupedByRoute = useMemo(() => {
    const groups = new Map();

    // Seed groups from route QRs
    textFilteredRoutes.forEach((item) => {
      const data = item.qr_data || {};
      const routeId = String(data.route_id || item.reference_id || item.id || "unknown");
      const routeName = data.route_name || item.reference_name || routeId;
      if (!groups.has(routeId)) {
        groups.set(routeId, { routeId, routeName, routeQR: null, checkpointQRs: [] });
      }
      groups.get(routeId).routeQR = item;
    });

    // Add checkpoint QRs into their route group
    textFilteredCheckpoints.forEach((item) => {
      const data = item.qr_data || {};
      const routeId = String(data.route_id || "unknown");
      const routeName = data.route_name || routeId;
      if (!groups.has(routeId)) {
        groups.set(routeId, { routeId, routeName, routeQR: null, checkpointQRs: [] });
      }
      groups.get(routeId).checkpointQRs.push(item);
    });

    // Sort groups by routeName
    return Array.from(groups.values()).sort((a, b) =>
      a.routeName.localeCompare(b.routeName)
    );
  }, [textFilteredRoutes, textFilteredCheckpoints]);

  // Apply route dropdown filter on top of grouped data
  const visibleGroups = useMemo(() => {
    if (selectedRoute === "all") return groupedByRoute;
    return groupedByRoute.filter((g) => g.routeId === selectedRoute);
  }, [groupedByRoute, selectedRoute]);

  const downloadQR = async (qrPath, fileName) => {
    if (!qrPath) {
      toast.error("No QR image available to download.");
      return;
    }

    try {
      const token = getToken();
      let imageUrl = qrPath;

      // If it's a relative path, construct the full URL
      if (!qrPath.startsWith("http")) {
        const baseUrl = API_BASE_URL.replace(/\/api$/, "");
        imageUrl = `${baseUrl}${qrPath}`;
      }

      const response = await fetch(imageUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download QR: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${fileName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      toast.success(`QR code "${fileName}" downloaded successfully!`);
    } catch (error) {
      console.error("QR download error:", error);
      toast.error("Failed to download QR image.");
    }
  };

  const handleRefresh = async () => {
    const token = getToken();
    await fetchPageData(dashboardData, token);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading patrol QR codes...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="qr-codes"
      activeMenu="qr-codes"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Patrol QR Code
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          View and download patrol checkpoint and route QR images
        </p>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Checkpoint QR Codes
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {stats.checkpointCount}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Route QR Codes
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.routeCount}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Total QR Codes
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.totalCount}
            </p>
          </div>
        </div>

        {/* Search + Filter Panel */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="flex justify-between items-center gap-4 flex-wrap mb-5">
            <div>
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Patrol QR Management
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Filter by route, then search within results
              </p>
            </div>

            <button
              type="button"
              onClick={handleRefresh}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Refresh
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedRoute}
              onChange={(e) => setSelectedRoute(e.target.value)}
              className="sm:w-64 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              style={{ fontFamily: FONT_UI }}
            >
              <option value="all">All Routes</option>
              {routeOptions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search by QR type, route, checkpoint, reference name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
              style={{ fontFamily: FONT_UI }}
            />
          </div>
        </div>

        {/* Grouped Route Sections */}
        {visibleGroups.length === 0 ? (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-center">
            <p className="text-[13px] text-[var(--text-muted)] m-0">
              No QR codes found for the selected filter.
            </p>
          </div>
        ) : (
          visibleGroups.map((group) => (
            <RouteGroup
              key={group.routeId}
              group={group}
              onDownload={downloadQR}
              qrImages={qrImages}
              loadingQr={loadingQr}
            />
          ))
        )}
      </div>
    </Layout>
  );
}

// ─── Route Group Section ──────────────────────────────────────────────────────
function RouteGroup({ group, onDownload, qrImages, loadingQr }) {
  const { routeName, routeQR, checkpointQRs } = group;

  return (
    <div className="mb-8">
      {/* Route group header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-[var(--divider)]" />
        <span
          className="px-4 py-1.5 rounded-full bg-[var(--btn-bg)] text-[var(--btn-text)] text-sm font-extrabold whitespace-nowrap"
          style={{ fontFamily: FONT_UI }}
        >
          {routeName}
        </span>
        <div className="h-px flex-1 bg-[var(--divider)]" />
      </div>

      {/* Route QR first */}
      {routeQR && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-4">
          <h3 className="text-[18px] font-extrabold text-[#22c55e] m-0 mb-5">
            Route QR
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            <QRCard
              item={routeQR}
              type="route"
              onDownload={onDownload}
              qrImages={qrImages}
              loadingQr={loadingQr}
            />
          </div>
        </div>
      )}

      {/* Checkpoint QRs after */}
      {checkpointQRs.length > 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <h3 className="text-[18px] font-extrabold text-[var(--accent)] m-0 mb-5">
            Checkpoint QRs
            <span className="ml-2 px-2.5 py-0.5 rounded-full bg-[var(--accent-faint)] text-[var(--accent)] text-xs font-extrabold">
              {checkpointQRs.length}
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {checkpointQRs.map((item, index) => (
              <QRCard
                key={`cp-${item.id || index}`}
                item={item}
                type="checkpoint"
                onDownload={onDownload}
                qrImages={qrImages}
                loadingQr={loadingQr}
              />
            ))}
          </div>
        </div>
      )}

      {/* Edge case: route has no QR and no checkpoints */}
      {!routeQR && checkpointQRs.length === 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-center">
          <p className="text-[13px] text-[var(--text-muted)] m-0">
            No QR codes available for this route.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Single QR Card ───────────────────────────────────────────────────────────
function QRCard({ item, type, onDownload, qrImages, loadingQr }) {
  const data = item.qr_data || {};

  const id =
    type === "checkpoint"
      ? data.checkpoint_id || item.reference_id || item.id
      : data.route_id || item.reference_id || item.id;

  const name =
    type === "checkpoint"
      ? data.checkpoint_name || item.reference_name || "-"
      : data.route_name || item.reference_name || "-";

  const routeId = data.route_id || item.reference_id || "-";
  const routeName = data.route_name || "-";
  const checkpointId = data.checkpoint_id || item.reference_id || "-";
  const checkpointName = data.checkpoint_name || item.reference_name || "-";
  const lat = data.lat ?? data.latitude ?? "-";
  const lng = data.lng ?? data.longitude ?? "-";

  const qrPath = item.qr_image_url || item.qr_code_id || item.qr_image || "";
  const isLoading = loadingQr[item.id] || false;
  const imageSrc = qrImages[item.id] || qrPath || "";

  return (
    <div className="bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-2xl p-5 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors">
      <div className="flex justify-between gap-3 mb-4">
        <div>
          <h4 className="m-0 text-lg font-extrabold text-[var(--text-primary)]">
            {name}
          </h4>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            {type === "checkpoint" ? "Patrol Checkpoint QR" : "Patrol Route QR"}
          </p>
        </div>

        <span
          className={`h-fit px-3 py-1.5 rounded-full text-xs font-extrabold ${
            type === "checkpoint"
              ? "bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]"
              : "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]"
          }`}
        >
          {item.qr_type || type}
        </span>
      </div>

      <div className="flex justify-center bg-[var(--surface)] rounded-2xl p-4 border border-[var(--surface-border)] mb-4 min-h-[200px]">
        {isLoading ? (
          <div className="w-[190px] h-[190px] flex items-center justify-center">
            <div className="w-8 h-8 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : imageSrc ? (
          <img
            src={imageSrc}
            alt={name}
            className="w-[190px] h-[190px] object-contain"
            onError={(e) => {
              // If image fails to load, try using the qr_image_url directly
              const fallbackUrl = item.qr_image_url || item.qr_code_id || item.qr_image;
              if (fallbackUrl && e.target.src !== fallbackUrl) {
                e.target.src = fallbackUrl;
              }
            }}
          />
        ) : (
          <div className="w-[190px] h-[190px] flex items-center justify-center text-[var(--text-muted)] text-sm">
            QR image not found
          </div>
        )}
      </div>

      <div className="space-y-1 text-sm text-[var(--text-muted)]">
        <p><b className="text-[var(--text-secondary)]">Record ID:</b> {item.id || "-"}</p>
        <p><b className="text-[var(--text-secondary)]">Reference ID:</b> {item.reference_id || "-"}</p>
        <p><b className="text-[var(--text-secondary)]">QR Type:</b> {item.qr_type || "-"}</p>

        {type === "checkpoint" ? (
          <>
            <p><b className="text-[var(--text-secondary)]">Route ID:</b> {routeId}</p>
            <p><b className="text-[var(--text-secondary)]">Route Name:</b> {routeName}</p>
            <p><b className="text-[var(--text-secondary)]">Checkpoint ID:</b> {checkpointId}</p>
            <p><b className="text-[var(--text-secondary)]">Checkpoint:</b> {checkpointName}</p>
            <p><b className="text-[var(--text-secondary)]">Lat / Lng:</b> {lat}, {lng}</p>
          </>
        ) : (
          <>
            <p><b className="text-[var(--text-secondary)]">Route ID:</b> {routeId}</p>
            <p><b className="text-[var(--text-secondary)]">Route Name:</b> {routeName}</p>
          </>
        )}
      </div>

      <button
        type="button"
        disabled={!qrPath}
        onClick={() =>
          onDownload(qrPath, `patrol_${type}_${id || "item"}`)
        }
        className="mt-4 w-full border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        style={{
          background: "var(--btn-bg)",
          color: "var(--btn-text)",
          boxShadow: "0 4px 18px var(--accent-glow)",
        }}
      >
        Download QR
      </button>
    </div>
  );
}